/**
 * Telling a TDO's `media` assets apart.
 *
 * A document TDO holds MORE THAN ONE `media` asset once the Redact Document OCR
 * engine has run: the file as it was ingested, and the searchable copy OCR
 * republished with an invisible text layer over it. The engine sets its copy
 * primary, because that is the one the editor, PII detection and export must
 * read (see `documents/README.md`).
 *
 * Which makes `primaryAsset(assetType: "media")` the WRONG answer for "download
 * the original file" — the question the folder view's menu asks. It used to be
 * the right answer only because a document had one media asset; the OCR stage
 * made that silently false, and the menu went on handing over the enriched copy
 * under the original's name.
 *
 * Both files are worth having, so both are offered. These helpers are the one
 * place that decides which record is which — pure, so the rule can be tested
 * without a TDO. They also decide which bytes the burn-in parses, which is why
 * `findPdfOriginalMediaAsset` refuses a record that can be shown not to be a
 * PDF before any other rule gets a say (`isNotPdf`).
 *
 * NAMING, which is a trap here. Everything in this file works on assets of type
 * `media`, and `findPdfOriginalMediaAsset` finds the un-enriched PDF among
 * them. It is NOT a lookup of the `original-media` ASSET TYPE — a separate
 * asset holding the file as the user uploaded it (a .docx the converter was
 * fed, or a video, audio or image file whose `media` asset the ingester
 * produced separately, see VE-30513). Attached only for non-PDF uploads: a PDF
 * upload gets no `original-media` because that would duplicate the `media`
 * asset, which already carries `details.isOriginalMedia`. The two live in
 * different asset types and answer different questions, and the shorter name
 * `findOriginalMediaAsset` read as the wrong one of them.
 */

import { isNil } from 'lodash';

/** The fields these helpers need; queries may select more. */
export interface MediaAssetRecord {
  readonly id: string;
  readonly name?: string | null;
  readonly contentType?: string | null;
  readonly createdDateTime?: string | null;
  readonly signedUri?: string | null;
  readonly details?: unknown;
  readonly sourceData?: { readonly engineId?: string | null } | null;
}

/**
 * Written by the fast-PDF upload path (`serviceCreatePdfMediaAsset`) onto the
 * asset it creates from the uploaded file. The strongest signal available — it
 * says "this IS the file the user handed us", rather than being inferred from
 * ordering.
 *
 * Absent on documents that went through the PDF converter, whose media asset
 * the engine creates, and on everything ingested before the marker existed.
 * Hence the fallbacks below; this is a preference, not a requirement.
 */
function isMarkedOriginal(asset: MediaAssetRecord): boolean {
  const details = asset.details;
  if (isNil(details) || typeof details !== 'object') {
    return false;
  }
  return (details as { isOriginalMedia?: unknown }).isOriginalMedia === true;
}

/**
 * Whether OCR produced this asset — the one thing that must never be mistaken
 * for the document as it was ingested.
 *
 * TWO INDEPENDENT SIGNALS, either sufficient:
 *   - `sourceData.engineId` matching the configured OCR engine. Available now,
 *     and the primary rule.
 *   - `details.isOcrEnriched`, which the engine is being updated to write.
 *     Checked already so it starts counting the day it ships, and so an asset
 *     whose `sourceData` is missing or re-stamped is still identifiable.
 *
 * Deliberately NOT "the newest" or "the primary one": OCR sets its copy primary,
 * so those would both name the enriched file on exactly the documents where
 * telling them apart matters.
 */
function isProducedByOcr(
  asset: MediaAssetRecord,
  ocrEngineId: string | undefined
): boolean {
  if (!!ocrEngineId && asset.sourceData?.engineId === ocrEngineId) {
    return true;
  }
  const details = asset.details;
  if (isNil(details) || typeof details !== 'object') {
    return false;
  }
  return (details as { isOcrEnriched?: unknown }).isOcrEnriched === true;
}

/**
 * Whether a record can be shown NOT to be a PDF.
 *
 * `findPdfOriginalMediaAsset` selects the bytes the burn-in feeds to MuPDF, and
 * `details.isOriginalMedia` — its most trusted signal — is written by BOTH
 * upload services now: onto the `media` asset by the fast-PDF path, and onto
 * the `original-media` asset holding the .docx / .eml / .msg by the converted
 * path. The only thing keeping those apart is each query's `assetType` filter,
 * which is one forgotten argument away from handing a Word document to a PDF
 * parser — and the failure would be a burn-in that throws mid-export, or worse,
 * a released file cut from the wrong bytes.
 *
 * A NEGATIVE test on purpose: it rejects what is demonstrably not a PDF rather
 * than demanding proof that something is one. `contentType` is selected by
 * every query that reaches these helpers, but it can be null on older ingests,
 * and refusing those would quietly push the export onto the enriched copy —
 * trading a hypothetical for the exact outcome the resolution exists to avoid.
 */
function isNotPdf(asset: MediaAssetRecord): boolean {
  const contentType = asset.contentType?.trim().toLowerCase();
  if (contentType) {
    // Parameters are legal on a media type (`application/pdf; version=1.7`).
    return contentType.split(';')[0]!.trim() !== 'application/pdf';
  }
  // No content type: fall back to the name, and only where it HAS an
  // extension to disagree with.
  const ext = asset.name
    ?.trim()
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/)?.[1];
  return !!ext && ext !== 'pdf';
}

/** Oldest first. A missing timestamp sorts last rather than throwing off the order. */
function oldestFirst(assets: readonly MediaAssetRecord[]): MediaAssetRecord[] {
  return [...assets].sort((a, b) =>
    (a.createdDateTime ?? '￿').localeCompare(b.createdDateTime ?? '￿')
  );
}

/**
 * The file as it was ingested, or null when none of the records can be shown to
 * be it — in which case the caller keeps whatever it already had rather than
 * downloading a guess.
 *
 * Three rules, most trustworthy first:
 *   1. the explicit `details.isOriginalMedia` marker, written by the fast-PDF
 *      upload onto the file the user handed us,
 *   2. failing that, the NEWEST asset OCR did not produce,
 *   3. failing that, null.
 *
 * Rule 2 is stated as "not produced by OCR" rather than by age or primacy.
 * Primacy is wrong because OCR sets its own copy primary. Age is wrong in both
 * directions: oldest would name a superseded conversion if a document is ever
 * re-converted, and newest-overall would name the enriched copy. What is wanted
 * is the current document minus the enrichment, which is exactly this.
 *
 * WHAT THIS RETURNS FOR A CONVERTED UPLOAD. A .docx is not a `media` asset —
 * only the PDF the converter produces from it is — so "original" here means the
 * converted PDF, not the file as the user had it on disk. Downloading that
 * source format would need a different asset (or the TDO's own details), and is
 * deliberately out of scope.
 */
export function findPdfOriginalMediaAsset(
  assets: readonly MediaAssetRecord[],
  ocrEngineId: string | undefined
): MediaAssetRecord | null {
  // Before either rule, and before the `isOriginalMedia` marker gets a say:
  // whatever this returns is parsed as a PDF. See `isNotPdf`.
  const ordered = oldestFirst(assets.filter((a) => !isNotPdf(a)));
  // Rule 1 takes the NEWEST marked asset, matching rule 2 below and
  // `findOriginalFormatAsset`: re-upload into an existing TDO leaves two marked
  // copies and the older one is superseded.
  const marked = [...ordered].reverse().find(isMarkedOriginal);
  if (marked) {
    return marked;
  }
  if (!ocrEngineId) {
    /**
     * REFUSE RATHER THAN GUESS. Without an engine id nothing here can be
     * identified as OCR's output (`details.isOcrEnriched` is not written yet),
     * so "newest non-OCR" degrades to "newest" — which on an enriched document
     * IS the OCR copy. Blanking `documentOcrEngineId` is the documented
     * rollback lever, and it must not silently turn the export's safety choice
     * into its opposite. Null makes callers keep what they had.
     */
    return null;
  }
  // NEWEST of what is left, not oldest. With one conversion the two agree; if a
  // document is ever re-converted, the newest non-enriched asset is the current
  // document and the oldest is a superseded copy of it.
  const candidates = ordered.filter((a) => !isProducedByOcr(a, ocrEngineId));
  return candidates[candidates.length - 1] ?? null;
}

/**
 * The searchable copy OCR published, or null if it has not published one.
 *
 * Newest wins: a re-run republishes, and the current searchable copy is the one
 * the editor is reading. Identified by `sourceData.engineId` only — never by
 * "the primary one" or "the newest one", either of which would hand over some
 * other engine's output as OCR's the first time another stage republishes.
 */
export function findOcrEnrichedMediaAsset(
  assets: readonly MediaAssetRecord[],
  ocrEngineId: string | undefined
): MediaAssetRecord | null {
  if (!ocrEngineId) {
    return null;
  }
  const matches = oldestFirst(assets).filter((a) =>
    isProducedByOcr(a, ocrEngineId)
  );
  return matches[matches.length - 1] ?? null;
}

/**
 * The file in the format it was UPLOADED in — the `original-media` asset.
 *
 * A different asset type from everything above, answering a different question:
 * those pick between PDFs on the `media` asset, this one returns the file the
 * user handed us (a .docx / .eml / .msg for a converted document, the video or
 * audio file itself for A/V).
 *
 * Attached only for non-PDF uploads, so a PDF never has one. For documents it
 * has been attached since VE-23391; for video, audio and images the Redact
 * upload saga attaches it (VE-30513), and on that path the ingester does not
 * write one of its own. Files ingested by other paths (Ingest from URL, the
 * BFF integrations) rely on the ingester, and A/V ingested on Oracle before
 * VE-30513 has none.
 *
 * Null therefore means "not available", never "error" — callers should omit
 * the option rather than show it broken.
 *
 * Newest wins, for a file re-uploaded into the same TDO.
 */
export function findOriginalFormatAsset(
  assets: readonly MediaAssetRecord[]
): MediaAssetRecord | null {
  const withUri = oldestFirst(assets).filter((a) => a.signedUri);
  return withUri[withUri.length - 1] ?? null;
}

/**
 * The extension to show on the download label — `docx` from `brief.docx`.
 *
 * Read off the asset's own name, which is the uploaded filename. Uppercased for
 * the menu, empty when there is nothing to show, so the caller can fall back to
 * a label with no format in it rather than printing "Download Original ()".
 */
export function originalFormatLabel(asset: MediaAssetRecord | null): string {
  const name = (asset?.name ?? '').trim();
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) {
    return '';
  }
  return name.slice(dot + 1).toUpperCase();
}

/**
 * The OCR copy's download name: `report.pdf` → `report[OCR-enriched].pdf`.
 *
 * Renaming is the whole point — both files are the same pages with the same
 * name, so saving them to one folder would otherwise leave the reviewer with
 * `report.pdf` and `report (1).pdf` and no way to tell which is which. The
 * suffix goes before the extension so the file still opens as a PDF.
 *
 * The engine's own asset name is ignored in favour of deriving from the name
 * shown in the folder view: the two need to correspond for the reviewer, and
 * the engine's is an implementation detail that has been seen to be a uuid.
 */
const OCR_ENRICHED_SUFFIX = '[OCR-enriched]';

export function ocrEnrichedFileName(originalName: string | undefined): string {
  const name = (originalName ?? '').trim();
  if (!name) {
    return `document${OCR_ENRICHED_SUFFIX}.pdf`;
  }
  const dot = name.lastIndexOf('.');
  // No extension, or a leading-dot name like `.profile` — nothing to insert
  // before, so append.
  if (dot <= 0) {
    return `${name}${OCR_ENRICHED_SUFFIX}`;
  }
  return `${name.slice(0, dot)}${OCR_ENRICHED_SUFFIX}${name.slice(dot)}`;
}
